import type { StructureNode } from "../api/rest";

export interface FlatNode {
  node: StructureNode;
  depth: number;
}

/**
 * ADR 173, slice S8. The ids of a node and its whole subtree, read from the pre-order flattened
 * rows: a node's descendants are the contiguous run of deeper rows immediately after it.
 */
export function subtreeIds(rows: FlatNode[], nodeId: string): Set<string> {
  const ids = new Set<string>();
  const start = rows.findIndex((r) => r.node.id === nodeId);
  if (start === -1) return ids;
  ids.add(nodeId);
  const baseDepth = rows[start].depth;
  for (let i = start + 1; i < rows.length && rows[i].depth > baseDepth; i++) ids.add(rows[i].node.id);
  return ids;
}

export interface ParentOption {
  id: string;
  label: string;
  depth: number;
}

/**
 * The nodes a given node may be re-pointed under: every node except itself and its descendants
 * (mirrors the backend cycle guard, so the UI never offers an illegal move). The root option
 * (parent = null) is added by the caller, which owns the i18n label.
 */
export function validParentOptions(rows: FlatNode[], nodeId: string): ParentOption[] {
  const banned = subtreeIds(rows, nodeId);
  return rows
    .filter((r) => !banned.has(r.node.id))
    .map(({ node, depth }) => ({ id: node.id, label: node.name || node.code || node.path, depth }));
}
